/** The command card (BAR's grid): a battle's orders, a selected barracks'
 *  build card, or a replay's viewer buttons. Its state is a store the game
 *  writes; its buttons call back into the game. */

import { For, Show, createSignal, type JSX } from "solid-js";
import { recipes } from "../state/recipes";
import { GoodIcon, RecipeFlow, RecipeIcon, RecipePicker } from "./RecipePicker";
import { UNLINKED_SVG } from "../ui/goodIcons";
import { createStore, reconcile } from "solid-js/store";
import { render } from "solid-js/web";

/** A button: label, key hint, and what it does. */
export interface CardButton {
  label: string;
  key: string;
  /** A tooltip, for the less obvious ones. */
  tip?: string;
  /** Small text under the label (a build's time). */
  small?: string;
  run: () => void;
  disabled?: boolean;
  /** Toggles and armed orders show pressed. */
  pressed?: boolean;
  /** The design a barracks makes now. */
  current?: boolean;
  /** A good's icon before the label (a trade post's goods). */
  icon?: string;
}

/** A good at a plant's station: what it holds against the room there. */
export interface Slot {
  good: string;
  n: number;
  cap: number;
  role: "in" | "out";
  /** Short of a cycle (an input) or at the cap (an output): why it's idle. */
  stuck: boolean;
}

export interface Card {
  /** none (nothing to command), orders, build (barracks), replay. */
  mode: "none" | "orders" | "build" | "replay";
  buttons: CardButton[];
  /** The build card's heading, and whether it warns (barracks waiting). */
  heading: string;
  warn: boolean;
  /** The build card's copy/paste tools. */
  tools: CardButton[];
  /** Barracks selected (v31.3): their limits, null where they differ;
   *  0 is none. */
  limits?: { keep: number | null; max: number | null; set: (field: "keep" | "max", v: number) => void } | null;
  /** Plants selected: what they make (a big tile; a click opens the
   *  picker), their lines, made last minute, and the pick. */
  recipe?: { current: string[]; lines: number; made: string; madeGood?: string; unlinked?: boolean; slots?: Slot[]; pick: (name: string) => void } | null;
}

const [card, setStore] = createStore<Card>({ mode: "none", buttons: [], heading: "", warn: false, tools: [] });
export { card };

/** Shows `next`: merged into what's shown (buttons by position), so a
 *  refresh changes only what differs and no button is rebuilt. */
export function setCard(next: Partial<Card>): void {
  setStore(reconcile({ ...card, heading: "", warn: false, tools: [], buttons: [], recipe: null, limits: null, ...next } as Card, { key: null, merge: true }));
}

function Button(b: CardButton & { focus: () => void }): JSX.Element {
  return (
    <button
      classList={{ current: !!b.current }}
      // Not `disabled`: a disabled button takes no pointer, so its tip
      // (why it can't be used) would never show.
      aria-disabled={b.disabled ? "true" : undefined}
      aria-pressed={b.pressed === undefined ? undefined : b.pressed}
      data-tip={b.tip}
      onClick={() => {
        if (b.disabled) return;
        b.run();
        b.focus();
      }}
    >
      <Show when={b.key}>
        <kbd>{b.key}</kbd>
      </Show>
      <Show when={b.icon}>{(g) => <GoodIcon good={g()} size={18} />}</Show>
      {b.label}
      <Show when={b.small}>
        <small>{b.small}</small>
      </Show>
    </button>
  );
}

/** A plant's recipe on its card: one big tile (several, if the selected
 *  plants make different things) with what goes in and comes out a minute;
 *  a click opens the picker above it. */
const short = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e4 ? `${Math.round(n / 1e3)}k` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(Math.round(n)));

/** A slot: the good's icon, how many, a bar against the room; marked when
 *  it's why the plant waits. */
function SlotView(props: Slot): JSX.Element {
  const tip = () =>
    `${props.good}: ${Math.round(props.n).toLocaleString("en-US")}${props.cap ? ` of ${props.cap.toLocaleString("en-US")} room` : ""} at the station.` +
    (props.stuck ? (props.role === "in" ? " Not enough for a cycle: trains must bring more." : " Full: trains must take it away.") : "");
  return (
    <span class="io-slot" classList={{ stuck: props.stuck, out: props.role === "out" }} data-tip={tip()}>
      <GoodIcon good={props.good} size={26} />
      <span class="io-n">{short(props.n)}</span>
      <Show when={props.cap}>
        <span class="io-bar">
          <span style={{ width: `${Math.min(100, (100 * props.n) / props.cap)}%` }} />
        </span>
      </Show>
    </span>
  );
}

function PlantRecipe(props: { current: string[]; lines: number; made: string; madeGood?: string; unlinked?: boolean; slots?: Slot[]; pick: (name: string) => void; focus: () => void }): JSX.Element {
  const [open, setOpen] = createSignal(false);
  let tile!: HTMLButtonElement;
  const one = () => (props.current.length === 1 ? recipes().find((r) => r.name === props.current[0]) : undefined);
  return (
    <div class="plant-recipe">
      <button ref={tile} class="plant-recipe-tile" data-recipe-opener aria-expanded={open()} onClick={() => setOpen(!open())}>
        <span class="plant-recipe-icons">
          <For each={props.current.slice(0, 4)}>{(n) => <RecipeIcon recipe={n} size={props.current.length > 1 ? 28 : 44} />}</For>
        </span>
        <span class="plant-recipe-text">
          <span class="plant-recipe-name">{props.current.length === 1 ? props.current[0] : `${props.current.length} recipes`}</span>
          <Show when={one()}>{(r) => <RecipeFlow r={r()} minute={true} lines={props.lines} />}</Show>
          <Show when={props.made}>
            <span class="plant-recipe-made" classList={{ unlinked: !!props.unlinked }}>
              <Show when={props.unlinked}>
                <span class="good-icon-wrap" innerHTML={UNLINKED_SVG(16)} />
              </Show>
              {props.made}
              <Show when={props.madeGood}>{(g) => <GoodIcon good={g()} size={16} />}</Show>
            </span>
          </Show>
        </span>
      </button>
      <Show when={props.slots?.length}>
        <div class="io-slots" aria-label="At the station">
          <For each={props.slots!.filter((x) => x.role === "in")}>{(x) => <SlotView {...x} />}</For>
          <Show when={props.slots!.some((x) => x.role === "in")}>
            <span class="recipe-arrow">→</span>
          </Show>
          <For each={props.slots!.filter((x) => x.role === "out")}>{(x) => <SlotView {...x} />}</For>
        </div>
      </Show>
      <Show when={open()}>
        <RecipePicker
          anchor={tile}
          align="right"
          current={props.current}
          close={() => {
            setOpen(false);
            props.focus();
          }}
          pick={(name) => {
            props.pick(name);
            setOpen(false);
            props.focus();
          }}
        />
      </Show>
    </div>
  );
}

/** One limit: a number (blank or 0: off) with a small stepper, applied on
 *  Enter, leaving the field, or a step. */
function LimitField(p: { label: string; unit: string; value: number | null; step: number; tip: string; set: (v: number) => void }): JSX.Element {
  let input!: HTMLInputElement;
  const commit = () => {
    const v = Math.max(0, Math.floor(Number(input.value) || 0));
    if (v !== (p.value ?? -1)) p.set(v);
  };
  const step = (d: number) => p.set(Math.max(0, (p.value ?? 0) + d));
  return (
    <label class="limit-field" data-tip={p.tip}>
      <span>{p.label}</span>
      <button class="ghost step" aria-label="Less" onClick={(e) => (e.preventDefault(), step(-p.step))}>
        −
      </button>
      <input
        ref={input}
        type="number"
        min="0"
        step={p.step}
        placeholder={p.value === null ? "mixed" : "off"}
        value={p.value ? String(p.value) : ""}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") commit();
        }}
        onBlur={commit}
      />
      <button class="ghost step" aria-label="More" onClick={(e) => (e.preventDefault(), step(p.step))}>
        +
      </button>
      <span class="muted">{p.unit}</span>
    </label>
  );
}

/** A barracks' limits (Factorio's assembler limits): steel to keep, and a
 *  cap on its design. */
function Limits(p: { keep: number | null; max: number | null; set: (field: "keep" | "max", v: number) => void }): JSX.Element {
  return (
    <div class="limits">
      <LimitField
        label="Keep"
        unit="steel"
        value={p.keep}
        step={500}
        tip="It makes a unit only if its station still has this much steel after paying for it (blank: no reserve)"
        set={(v) => p.set("keep", v)}
      />
      <LimitField
        label="Up to"
        unit="of this design"
        value={p.max}
        step={10}
        tip="It stops while you have this many or more of its design alive, anywhere (blank: no cap; counts refresh each game minute)"
        set={(v) => p.set("max", v)}
      />
    </div>
  );
}

function CommandCard(): JSX.Element {
  // After a click the map keeps the keyboard (orders by key go on working).
  const focus = () => document.getElementById("view")?.focus();
  return (
    <>
      <Show when={card.mode === "build"}>
        <div class="build-bar" role="toolbar" aria-label="What the selected barracks make">
          <span class="build-label" classList={{ warn: card.warn }}>
            {card.heading}
          </span>
          <Show when={card.recipe}>{(r) => <PlantRecipe {...r()} focus={focus} />}</Show>
          <For each={card.buttons}>{(b) => <Button {...b} focus={focus} />}</For>
          <For each={card.tools}>{(b) => <Button {...b} focus={focus} />}</For>
          <Show when={card.limits}>{(l) => <Limits {...l()} />}</Show>
        </div>
      </Show>
      <Show when={card.mode === "orders" || card.mode === "replay"}>
        <div class="order-grid" role="toolbar" aria-label="Orders">
          <For each={card.buttons}>{(b) => <Button {...b} focus={focus} />}</For>
        </div>
      </Show>
    </>
  );
}

render(() => <CommandCard />, document.getElementById("command-card")!);
